import type {
	NativeUploadReconciliationBatch,
	NativeUploadReconciliationItem,
	UploadProgressMessage,
	UploadProgressPresentationStage,
} from '@sosocio/creator-types/app';
import {
	isUploadProgressMessageV2,
	parseUploadProgressMessage,
} from '@sosocio/tools/external-upload';
export {
	buildExternalUploadChannel,
	getExternalUploadRetryDelay,
	getUploadProgressIdentityKey,
	isUploadProgressMessageV2,
	parseUploadProgressMessage,
	shouldAcceptUploadProgress,
	type UploadProgressIdentity,
} from '@sosocio/tools/external-upload';

export type UploadProgressDialogProps = {
	photos: string[];
	progressPercentage: number;
	stage: UploadProgressPresentationStage;
	standalone: boolean;
	submittedCount: number;
};

export type ProgressDialogContext = {
	isUploadOnly: boolean;
	isFromExternalUpload: boolean;
	isDialogOpen: boolean;
	/**
	 * Whether the user previously dismissed this external upload's progress
	 * dialog manually. Reset to `false` only when a fresh external upload
	 * session starts (e.g. {@link ExternalUploadController.showQRCode}).
	 */
	wasDismissed: boolean;
};

/**
 * Checks whether an optional numeric native-upload field is finite and non-negative.
 *
 * @param value - Unknown optional numeric value.
 * @returns Whether the value is absent or a supported non-negative number.
 */
function isOptionalNonNegativeNumber(value: unknown): value is number | undefined {
	return (
		typeof value === 'undefined'
		|| (
			typeof value === 'number'
			&& Number.isFinite(value)
			&& value >= 0
		)
	);
}

/**
 * Narrows an unknown JSON value to an object with string keys.
 *
 * @param value - Unknown runtime value.
 * @returns Whether the value is a non-null, non-array object.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
	return (
		typeof value === 'object'
		&& value !== null
		&& !Array.isArray(value)
	);
}

/**
 * Clamps a progress percentage to the supported finite range.
 *
 * @param value Raw progress percentage.
 * @returns Finite progress percentage from zero through one hundred.
 */
function sanitizeProgressPercentage(value: number): number {
	if (!Number.isFinite(value)) {
		return 0;
	}

	return Math.min(
		100,
		Math.max(
			0,
			value,
		),
	);
}

/**
 * Normalizes the number of submitted upload items for presentation state.
 *
 * @param value Raw submitted item count.
 * @returns Finite non-negative count.
 */
function sanitizeSubmittedCount(value: number): number {
	if (!Number.isFinite(value)) {
		return 0;
	}

	return Math.max(
		0,
		value,
	);
}

/**
 * Produces determinate presentation progress for either protocol version.
 * Protocol v2 may omit percentage during indeterminate stages, so its
 * transferred/submitted counters provide the safe fallback.
 *
 * @param message - Validated upload progress payload.
 * @returns Clamped transfer percentage from zero through one hundred.
 */
export function normalizeUploadProgressPercentage(message: UploadProgressMessage): number {
	if (typeof message.progressPercentage === 'number') {
		return sanitizeProgressPercentage(message.progressPercentage);
	}

	if (!isUploadProgressMessageV2(message)) {
		return 0;
	}

	if (message.submittedCount <= 0) {
		return message.stage === 'completed' ? 100 : 0;
	}

	return sanitizeProgressPercentage(
		100 * message.transferredCount / message.submittedCount,
	);
}

/**
 * Validates one native item used to rebuild an upload actor after a WebView reload.
 *
 * @param value - Untrusted native bridge payload.
 * @returns A normalized reconciliation item, or `null` when validation fails.
 */
function parseNativeUploadReconciliationItem(value: unknown): NativeUploadReconciliationItem | null {
	if (
		!isRecord(value)
		|| typeof value.uploadId !== 'string'
		|| value.uploadId.length === 0
		|| typeof value.name !== 'string'
		|| typeof value.totalBytes !== 'number'
		|| !Number.isFinite(value.totalBytes)
		|| value.totalBytes <= 0
		|| typeof value.uploadedBytes !== 'number'
		|| !Number.isFinite(value.uploadedBytes)
		|| value.uploadedBytes < 0
		|| value.uploadedBytes > value.totalBytes
		|| (
			typeof value.localPhotoId !== 'undefined'
			&& (
				typeof value.localPhotoId !== 'string'
				|| value.localPhotoId.length === 0
			)
		)
		|| (
			typeof value.uploadUrl !== 'undefined'
			&& (
				typeof value.uploadUrl !== 'string'
				|| value.uploadUrl.length === 0
			)
		)
		|| !isOptionalNonNegativeNumber(value.width)
		|| !isOptionalNonNegativeNumber(value.height)
		|| !isOptionalNonNegativeNumber(value.uploadStart)
	) {
		return null;
	}

	return {
		height: value.height,
		localPhotoId: value.localPhotoId,
		name: value.name,
		totalBytes: value.totalBytes,
		uploadedBytes: value.uploadedBytes,
		uploadId: value.uploadId,
		uploadStart: value.uploadStart,
		uploadUrl: value.uploadUrl,
		width: value.width,
	};
}

/**
 * Validates the authoritative native snapshot required to rebuild upload actors.
 * Non-terminal batches must account for every submitted photo through either an
 * active item or a unique completed photo. Item identifiers must also be unique
 * so one callback cannot ambiguously address multiple actors.
 *
 * @param value - Untrusted native bridge payload.
 * @returns A normalized reconciliation batch, or `null` when validation fails.
 */
export function parseNativeUploadReconciliationBatch(value: unknown): NativeUploadReconciliationBatch | null {
	if (!isRecord(value) || !Array.isArray(value.items)) {
		return null;
	}

	const progress = parseUploadProgressMessage(value.progress);

	if (!progress || !isUploadProgressMessageV2(progress)) {
		return null;
	}

	const items = value.items.map(parseNativeUploadReconciliationItem);

	if (items.some((item) => item === null)) {
		return null;
	}

	const validItems = items as NativeUploadReconciliationItem[];
	const uploadIds = new Set(validItems.map((item) => item.uploadId));
	const localPhotoIds = validItems
		.map((item) => item.localPhotoId)
		.filter((id): id is string => typeof id === 'string');
	const completedPhotoIds = new Set((progress.photos ?? []).map((photo) => (
		`${typeof photo.id}:${photo.id}`
	)));
	const terminal = (
		progress.stage === 'completed'
		|| progress.stage === 'failed'
		|| progress.stage === 'aborted'
	);
	const nonTerminalBatchAccounted = (
		progress.submittedCount > 0
		&& completedPhotoIds.size === (progress.photos?.length ?? 0)
		&& validItems.length + completedPhotoIds.size === progress.submittedCount
	);

	if (
		uploadIds.size !== validItems.length
		|| new Set(localPhotoIds).size !== localPhotoIds.length
		|| (!terminal && !nonTerminalBatchAccounted)
	) {
		return null;
	}

	return {
		items: validItems,
		progress,
	};
}

/**
 * Returns true when the progress dialog should be opened for this tick. The
 * dialog only ever opens for uploadOnly or external-source progress, when
 * there are submitted items still in flight (`progressPercentage < 100`), and
 * when the user has not manually dismissed the dialog already.
 *
 * @param data Validated progress payload.
 * @param context Caller mode, ownership, and dismissal state.
 * @returns Whether this source should create a progress dialog.
 */
export function shouldOpenProgressDialog(
	data: UploadProgressMessage,
	context: ProgressDialogContext,
): boolean {
	const progressPercentage = normalizeUploadProgressPercentage(data);
	const submittedCount = sanitizeSubmittedCount(data.submittedCount);

	if (context.isDialogOpen) {
		return false;
	}

	if (context.wasDismissed) {
		return false;
	}

	if (!submittedCount) {
		return false;
	}

	if (
		isUploadProgressMessageV2(data)
			? (
				data.stage === 'completed'
				|| data.stage === 'failed'
				|| data.stage === 'aborted'
			)
			: progressPercentage >= 100
	) {
		return false;
	}

	return context.isUploadOnly || context.isFromExternalUpload;
}

/**
 * Apply a progress tick to the live reactive props in place. The returned
 * tuple describes which fields actually changed — useful for tests asserting
 * minimal mutation. Photo url comparisons are positional + identity to keep
 * Vue's array-reactivity stable when nothing has actually changed.
 *
 * @param props Reactive dialog state mutated in place.
 * @param data Validated progress payload.
 * @returns Field-level mutation flags for diagnostics/tests.
 */
export function applyProgressUpdate(
	props: UploadProgressDialogProps,
	data: UploadProgressMessage,
): {
	progressPercentageChanged: boolean;
	submittedCountChanged: boolean;
	photosChanged: boolean;
	stageChanged: boolean;
} {
	let progressPercentageChanged = false;
	let submittedCountChanged = false;
	let photosChanged = false;
	let stageChanged = false;
	const progressPercentage = normalizeUploadProgressPercentage(data);

	if (progressPercentage !== props.progressPercentage) {
		props.progressPercentage = progressPercentage;
		progressPercentageChanged = true;
	}

	if (
		typeof data.submittedCount === 'number'
	) {
		const submittedCount = sanitizeSubmittedCount(data.submittedCount);

		if (submittedCount !== props.submittedCount) {
			props.submittedCount = submittedCount;
			submittedCountChanged = true;
		}
	}

	if (data.photos) {
		const photoUrls = data.photos
			.map((photo) => photo.url)
			.filter((url): url is string => !!url);
		const same = (
			photoUrls.length === props.photos.length
			&& photoUrls.every((url, index) => url === props.photos[index])
		);

		if (!same) {
			props.photos = photoUrls;
			photosChanged = true;
		}
	}

	const stage = isUploadProgressMessageV2(data)
		? data.stage
		: data.progressPercentage >= 100
			? 'completed'
			: data.progressPercentage <= 0
				? 'selected'
				: 'transferring';

	if (stage !== props.stage) {
		props.stage = stage;
		stageChanged = true;
	}

	return {
		progressPercentageChanged,
		submittedCountChanged,
		photosChanged,
		stageChanged,
	};
}

export function buildExternalUploadMutation(
	channel: string,
	url: string,
	event: 'upload' | 'complete' | 'abort' | 'close',
	payload: UploadProgressMessage | Record<string, never>,
): {
	type: 'external_upload';
	channel: string;
	url: string;
	event: typeof event;
	payload: typeof payload;
} {
	return {
		type: 'external_upload',
		channel,
		url,
		event,
		payload,
	};
}
